import { useCallback, useEffect, useMemo, useState } from 'react';
import { knowledgeApi } from '../../supportApi';
import {
  loadViewLaterItems,
  saveViewLaterItems,
  threadToViewLaterItem,
  upsertViewLaterItem,
  removeViewLaterItem,
} from '../storage/viewLaterStorage';

export function useDiscussionViewLater(profile, addNotification) {
  const [viewLaterItems, setViewLaterItems] = useState([]);
  const [viewLaterReady, setViewLaterReady] = useState(() => !profile?.userId);

  const viewLaterSlugSet = useMemo(
    () => new Set(viewLaterItems.map((item) => String(item?.slug || '').trim()).filter(Boolean)),
    [viewLaterItems]
  );

  const persistViewLaterItems = useCallback(
    (nextItems) => {
      setViewLaterItems(nextItems);
      saveViewLaterItems(profile?.userId, nextItems);
    },
    [profile?.userId]
  );

  const applyViewLaterFromServer = useCallback(
    (items) => {
      const list = Array.isArray(items) ? items.filter((item) => item?.slug) : [];
      persistViewLaterItems(list);
      return list;
    },
    [persistViewLaterItems]
  );

  useEffect(() => {
    if (!profile?.userId) {
      setViewLaterItems([]);
      setViewLaterReady(true);
      return undefined;
    }
    setViewLaterReady(false);
    setViewLaterItems(loadViewLaterItems(profile.userId));
    let cancelled = false;
    (async () => {
      try {
        const data = await knowledgeApi.discussionsViewLaterList();
        if (cancelled) return;
        const serverItems = Array.isArray(data?.items) ? data.items.filter((item) => item?.slug) : [];
        const local = loadViewLaterItems(profile.userId);
        const serverSlugs = new Set(serverItems.map((item) => String(item.slug).trim()));
        for (const item of local) {
          const slug = String(item?.slug || '').trim();
          if (!slug || serverSlugs.has(slug)) continue;
          try {
            await knowledgeApi.discussionsViewLaterAdd(slug);
          } catch {
            /* skip */
          }
        }
        const fresh = await knowledgeApi.discussionsViewLaterList();
        if (cancelled) return;
        const freshItems = Array.isArray(fresh?.items) ? fresh.items.filter((item) => item?.slug) : [];
        if (freshItems.length) {
          applyViewLaterFromServer(freshItems);
        } else if (local.length) {
          setViewLaterItems(local);
          saveViewLaterItems(profile.userId, local);
        } else {
          applyViewLaterFromServer([]);
        }
      } catch {
        if (!cancelled) setViewLaterItems(loadViewLaterItems(profile.userId));
      } finally {
        if (!cancelled) setViewLaterReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.userId, applyViewLaterFromServer]);

  const toggleViewLaterFromFeed = useCallback(
    async (thread) => {
      const s = String(thread?.slug || '').trim();
      if (!s || !profile?.userId) return;
      if (viewLaterSlugSet.has(s)) {
        const prev = viewLaterItems;
        persistViewLaterItems(removeViewLaterItem(prev, s));
        try {
          const data = await knowledgeApi.discussionsViewLaterRemove(s);
          applyViewLaterFromServer(data?.items);
        } catch (e) {
          persistViewLaterItems(prev);
          addNotification?.({
            theme: 'yellow',
            headingText: 'View later',
            message: e?.message || 'Could not remove saved post.',
          });
        }
        return;
      }
      const item = threadToViewLaterItem(thread);
      if (!item) return;
      const prev = viewLaterItems;
      persistViewLaterItems(upsertViewLaterItem(prev, item));
      try {
        const data = await knowledgeApi.discussionsViewLaterAdd(s);
        applyViewLaterFromServer(data?.items);
      } catch (e) {
        persistViewLaterItems(prev);
        addNotification?.({
          theme: 'yellow',
          headingText: 'View later',
          message: e?.message || 'Could not save post for later.',
        });
      }
    },
    [profile?.userId, viewLaterItems, viewLaterSlugSet, persistViewLaterItems, applyViewLaterFromServer, addNotification]
  );

  const removeViewLaterItemBySlug = useCallback(
    async (slug, e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      const s = String(slug || '').trim();
      if (!s || !profile?.userId) return;
      const prev = viewLaterItems;
      persistViewLaterItems(removeViewLaterItem(prev, s));
      try {
        const data = await knowledgeApi.discussionsViewLaterRemove(s);
        applyViewLaterFromServer(data?.items);
      } catch (err) {
        persistViewLaterItems(prev);
        addNotification?.({
          theme: 'yellow',
          headingText: 'View later',
          message: err?.message || 'Could not remove saved post.',
        });
      }
    },
    [profile?.userId, viewLaterItems, persistViewLaterItems, applyViewLaterFromServer, addNotification]
  );

  return {
    viewLaterItems,
    viewLaterSlugSet,
    viewLaterReady,
    toggleViewLaterFromFeed,
    removeViewLaterItemBySlug,
  };
}
